import {
  RADIUS_XL,
  WEB_FONT_FORM_FIELD_ERROR,
  WEB_FONT_FORM_FIELD_INPUT,
  WEB_FONT_FORM_FIELD_LABEL,
  WEB_GREY_REG,
  WEB_PURPLE_EXTRA_LIT,
  WEB_PURPLE_REG,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { useId } from 'react';
import styled, { css } from 'styled-components';
import type { InputProps } from './index.type';

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: ${WEB_PURPLE_REG};
  font-weight: 600;
  font-size: ${WEB_FONT_FORM_FIELD_LABEL};
  margin-bottom: 0.5rem;
  padding-left: 1rem;
`;

const RequiredAsterisk = styled.span`
  color: ${WEB_RED_REG};
  font-weight: 400;
  margin-left: 0.125rem;
`;

const LabelDescription = styled.span`
  color: ${WEB_PURPLE_REG};
  font-weight: 400;
`;

const StyledInput = styled.input<{ $hasError: boolean }>`
  border: 0;
  border-radius: ${RADIUS_XL};
  ${({ $hasError }) =>
    $hasError &&
    css`
      outline: 2px solid ${WEB_RED_REG};
    `}
  background: rgba(0, 0, 0, 0.05);
  padding: 0.5rem 1rem;
  font-size: ${WEB_FONT_FORM_FIELD_INPUT};
  line-height: 150%;

  &:focus {
    outline: 2px solid ${WEB_PURPLE_EXTRA_LIT};
  }
  &::placeholder {
    color: ${WEB_GREY_REG};
    font-size: inherit;
    font-weight: 400;
  }
`;

const ErrorText = styled.div`
  color: ${WEB_RED_REG};
  font-size: ${WEB_FONT_FORM_FIELD_ERROR};
  padding-left: 1rem;
  margin-top: 0.25rem;
`;

const Input = ({
  name,
  type,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  label,
  labelDescription,
  required,
  placeholder,
  id,
}: InputProps) => {
  const inputId = useId();
  const errorTextId = useId();
  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={inputId}>
          {label}
          {labelDescription && (
            <>
              {' '}
              <LabelDescription>({labelDescription})</LabelDescription>
            </>
          )}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <StyledInput
        id={id ?? inputId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        $hasError={!!error}
        aria-invalid={!!error}
        aria-errormessage={errorTextId}
        required={required}
      />
      {error && <ErrorText id={errorTextId}>{error}</ErrorText>}
    </InputWrapper>
  );
};

export default Input;
